import { FC, useState } from 'react';

import { AlertDialogWrapper } from '../../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { SelectedCompanyCurrency } from '../../../../../components/selected-company-currency/index';
import { Tooltip } from '../../../../../components/tooltip/index';
import { EventHooks, useFormat, useModalState } from '../../../../../hooks/index';
import { StringKey } from '../../../../../lang/index';
import { EventTypeBackend, ExerciseEventItem } from '../../../../../types/events.types';
import { SharePlanType } from '../../../../../types/pool-plans.types';
import {
  downloadCompanyStorageFilesSequentially,
  openCompanyStorageUrlInNewTab,
} from '../../../../../utils/company-storage-url.util';
import { toNumber } from '../../../../../utils/to-number.util';
import { EventTransactionFilesCell } from '../../../components/event-transaction-files-cell';
import { FilesModal } from '../../../modals/index';
import { resolveExerciseTransactionFilesLinks } from '../../../utils/resolve-event-document-links.util';
import { BasicTable } from './basic-table';
import {
  getTransactionsTableStateProps,
  itemsToFetchMap,
  TableBodyProps,
} from './event-table-types.types';
import { PaginationItem } from './pagination-item';
import { EventSignatureStatusTableCell, Td } from './table-td';

const planTypeTranslationKey: Record<SharePlanType, StringKey> = {
  [SharePlanType.GROWTH_HURDLE]: 'HURDLE_APPROVAL',
  [SharePlanType.BSPCE]: 'EXERCISE_BSPCE',
  [SharePlanType.COMMON_STOCK]: 'EXERCISE',
  [SharePlanType.PHANTOM]: 'EXERCISE',
  [SharePlanType.RSA]: 'EXERCISE',
  [SharePlanType.RSU]: 'EXERCISE',
  [SharePlanType.STOCK_OPTIONS]: 'EXERCISE_STOCK_OPTIONS',
  [SharePlanType.SAR]: 'HURDLE_APPROVAL',
  [SharePlanType.WARRANTS]: 'EXERCISE_EXPIRE_WARRANTS',
};

export const Exercised: FC<TableBodyProps> = (props) => {
  const { format } = useFormat();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsToFetch, setItemsToFetch] = useState(itemsToFetchMap[0]);
  const [activeFilesModal, setActiveFilesModal] = useState<{
    createdAt?: Date;
    docusignFileLinks: string[];
    filesLinks: string[];
    stakeholderName: string;
  } | null>(null);

  const { items, totalPages, isLoading } = EventHooks.useTransactions<ExerciseEventItem>({
    eventId: props.eventId,
    eventType: EventTypeBackend.EXERCISE,
    companyId: props.companyId,
    currentPage,
    take: Number(itemsToFetch),
    dateFilter: props.selectedDateFilter,
    customDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
  });

  const tableStateProps = getTransactionsTableStateProps({
    selectedDateFilter: props.selectedDateFilter,
    selectedCustomDateFilter: props.selectedCustomDateFilter,
    transactionView: props.transactionView,
    isLoading,
    itemsCount: items.length,
    event: props,
    itemsToFetch: Number(itemsToFetch),
  });

  const {
    isOpen: isOpenFilesModal,
    handleCloseModal: handleCloseFilesModal,
    handleOpenModal: handleOpenFilesModal,
    toggler: filesModalToggler,
  } = useModalState();

  const handleDocumentClick = (link: string) => {
    void openCompanyStorageUrlInNewTab(props.companyId, link);
  };

  const handleDownloadAllDocuments = async (links: string[]) => {
    await downloadCompanyStorageFilesSequentially(props.companyId, links);
  };

  const handleOpenFilesModalForRow = (
    filesLinks: string[],
    stakeholderName: string,
    date: Date,
    docusignFileLinks: string[],
  ) => {
    setActiveFilesModal({
      filesLinks,
      stakeholderName,
      createdAt: date,
      docusignFileLinks,
    });
    handleOpenFilesModal();
  };

  const handleCloseFilesModalWithReset = () => {
    setActiveFilesModal(null);
    handleCloseFilesModal();
  };

  const renderFilesCell = (item: ExerciseEventItem, eventFilesLinks: string[]) => (
    <EventTransactionFilesCell
      date={item.date}
      docusignFileLinks={item.docusignFileLinks}
      filesLinks={resolveExerciseTransactionFilesLinks(item, eventFilesLinks)}
      linkedSignatureFileLink={item.linkedSignatureFileLink}
      linkedSignatureId={item.linkedSignatureId}
      onDocumentClick={handleDocumentClick}
      onOpenFilesModal={handleOpenFilesModalForRow}
      signatureStatus={item.signatureStatus}
      stakeholderName={item.vestingTask.grantItem.stakeholder.fullName}
    />
  );

  if (props.type !== EventTypeBackend.EXERCISE && props.type !== EventTypeBackend.APPROVAL)
    return <></>;

  return (
    <>
      <AlertDialogWrapper control={{ onOpenChange: filesModalToggler, open: isOpenFilesModal }}>
        <FilesModal
          createdAt={activeFilesModal?.createdAt}
          docusignFileLinks={activeFilesModal?.docusignFileLinks}
          filesLinks={activeFilesModal?.filesLinks || []}
          name={props.name}
          onClose={handleCloseFilesModalWithReset}
          onDownloadFile={handleDocumentClick}
          onSuccess={() => handleDownloadAllDocuments(activeFilesModal?.filesLinks || [])}
          stakeholderName={activeFilesModal?.stakeholderName || ''}
        />
      </AlertDialogWrapper>
      <BasicTable
        eventType={EventTypeBackend.EXERCISE}
        transactionView={props.transactionView}
        {...tableStateProps}
      >
        {items.map((item) => {
          const {
            id,
            exercised,
            vestingTask,
            date,
            payout,
            signatureSignedCount,
            signatureStatus,
            signatureTotalCount,
            exerciseEvent: {
              event: { filesLinks },
            },
          } = item;
          const planType = vestingTask?.grantItem?.plan?.type;
          const signatureStatusCell = (
            <EventSignatureStatusTableCell
              signatureSignedCount={signatureSignedCount}
              signatureStatus={signatureStatus}
              signatureTotalCount={signatureTotalCount}
            />
          );

          if (planType === SharePlanType.GROWTH_HURDLE)
            return (
              <tr key={id}>
                <Td value={format(date, 'dd/MM/yyyy')} />
                <Td value={<AppFormattedMessage id={planTypeTranslationKey[planType]} />} />
                <Td
                  value={
                    <Tooltip content={vestingTask.grantItem.stakeholder.fullName}>
                      <span>{vestingTask.grantItem.stakeholder.fullName}</span>
                    </Tooltip>
                  }
                />
                <Td className="uppercase" value={vestingTask.grantItem.id.split('-')[0]} />
                <Td value={toNumber(exercised).toLocaleString('en-US')} />
                <Td value={vestingTask.grantItem.plan.pool.shareClass.name} />
                {signatureStatusCell}
                {!props.transactionView && <Td value={renderFilesCell(item, filesLinks ?? [])} />}
              </tr>
            );

          if (planType === SharePlanType.STOCK_OPTIONS || planType === SharePlanType.BSPCE)
            return (
              <tr key={id}>
                <Td value={format(date, 'dd/MM/yyyy')} />
                <Td value={<AppFormattedMessage id={planTypeTranslationKey[planType]} />} />
                <Td
                  value={
                    <Tooltip content={vestingTask.grantItem.stakeholder.fullName}>
                      <span>{vestingTask.grantItem.stakeholder.fullName}</span>
                    </Tooltip>
                  }
                />
                <Td className="uppercase" value={vestingTask.grantItem.id.split('-')[0]} />
                <Td value={toNumber(exercised).toLocaleString('en-US')} />
                <Td value={vestingTask.grantItem.plan.pool.shareClass.name} />
                {signatureStatusCell}
                {!props.transactionView && <Td value={renderFilesCell(item, filesLinks ?? [])} />}
              </tr>
            );

          if (planType === SharePlanType.WARRANTS)
            return (
              <tr key={id}>
                <Td value={format(date, 'dd/MM/yyyy')} />
                <Td value={<AppFormattedMessage id={planTypeTranslationKey[planType]} />} />
                <Td
                  value={
                    <Tooltip content={vestingTask.grantItem.stakeholder.fullName}>
                      <span>{vestingTask.grantItem.stakeholder.fullName}</span>
                    </Tooltip>
                  }
                />
                <Td className="uppercase" value={vestingTask.grantItem.id.split('-')[0]} />
                <Td
                  value={(
                    exercised / toNumber(vestingTask.grantItem.plan.conversionRatio, 1)
                  ).toLocaleString('en-US')}
                />
                <Td value={vestingTask.grantItem.plan.pool.shareClass.name} />
                {signatureStatusCell}
                {!props.transactionView && <Td value={renderFilesCell(item, filesLinks ?? [])} />}
              </tr>
            );

          if (planType === SharePlanType.SAR)
            return (
              <tr key={id}>
                <Td value={format(date, 'dd/MM/yyyy')} />
                <Td value={<AppFormattedMessage id={planTypeTranslationKey[planType]} />} />
                <Td
                  value={
                    <Tooltip content={vestingTask.grantItem.stakeholder.fullName}>
                      <span>{vestingTask.grantItem.stakeholder.fullName}</span>
                    </Tooltip>
                  }
                />
                <Td className="uppercase" value={vestingTask.grantItem.id.split('-')[0]} />
                <Td value={exercised.toLocaleString('en-US')} />
                <Td value={vestingTask.grantItem.plan.pool.shareClass.name} />
                <Td
                  value={
                    <>
                      <SelectedCompanyCurrency />
                      {payout && payout.toLocaleString('en-US')}
                    </>
                  }
                />
                {signatureStatusCell}
                {!props.transactionView && <Td value={renderFilesCell(item, filesLinks ?? [])} />}
              </tr>
            );

          return null;
        })}
      </BasicTable>
      <PaginationItem
        currentPage={currentPage}
        handleChangeCurrentPage={setCurrentPage}
        handleFetchAmountChange={setItemsToFetch}
        itemsCount={items.length}
        itemsToFetch={itemsToFetch}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
      />
    </>
  );
};
